import round from 'lodash/round';

import { CURRENCIES } from '@savgroup-front-common/constants';
import { adaptQuotationNewModelDto } from '@savgroup-front-common/core/components/Quotation/adapters/adaptQuotationNewModelDto';
import { ReverseMoneyHelper } from '@savgroup-front-common/core/helpers/ReverseMoneyHelper';
import { useGetOwnerDeductibleAndAlreadyPaidAmount } from '@savgroup-front-common/core/hooks/useGetOwnerDeductibleAndAlreadyPaidAmount';
import {
  FileSummary,
  QuotationState,
  RecipientType,
} from '@savgroup-front-common/types';

import useGetSellerConfiguration from '../../../../../../../../app/hooks/useGetSellerConfiguration';
import { useGetCustomerFileFullSummaryQuery } from '../../../../../../hooks/useGetCustomerFileFullSummaryQuery';
import useGetVatValueBySellerCountry from '../../../../../../hooks/useGetVatValueBySellerCountry';

interface UseCalculateQuotationTotalsArgs {
  quotation: ReturnType<typeof adaptQuotationNewModelDto>[0];
  fileId: string;
}

export const useCalculateQuotationTotals = ({
  quotation,
  fileId,
}: UseCalculateQuotationTotalsArgs) => {
  const { fileInfo } = useGetCustomerFileFullSummaryQuery({ fileId });
  const { vatRate } = useGetVatValueBySellerCountry({
    fileSummary: fileInfo as FileSummary,
  });
  const { alreadyPaidAmountInPreviousQuotation, deductibleAmountFromClaim } =
    useGetOwnerDeductibleAndAlreadyPaidAmount({ fileId });

  const totalAdministrativeFees = (quotation.administrativeFees || []).reduce(
    (acc, administrativeFee) => {
      if (administrativeFee.recipient?.recipientType !== RecipientType.OWNER) {
        return acc;
      }

      const newAmount = administrativeFee.preTaxAmount.amount;
      const newAmountIncludedTax = administrativeFee.amountIncludedTax.amount;

      return {
        totalPreVat: acc.totalPreVat + newAmount,
        totalVat: acc.totalVat + newAmountIncludedTax,
      };
    },
    { totalVat: 0, totalPreVat: 0 },
  );
  const totalService = (quotation.services || []).reduce(
    (acc, service) => {
      if (service.recipient?.recipientType !== RecipientType.OWNER) {
        return acc;
      }

      const newAmount = service.preTaxAmount;
      const newAmountIncludedTax = service.amountIncludedTax;

      return {
        totalPreVat: acc.totalPreVat + newAmount.amount,
        totalVat: acc.totalVat + newAmountIncludedTax.amount,
      };
    },
    { totalVat: 0, totalPreVat: 0 },
  );
  const totalUnknownService = (quotation.unknownServices || []).reduce(
    (acc, unknownService) => {
      if (unknownService.recipient?.recipientType !== RecipientType.OWNER) {
        return acc;
      }

      const newAmount = unknownService.preTaxAmount.amount;
      const newAmountIncludedTax = unknownService.amountIncludedTax.amount;

      return {
        totalPreVat: acc.totalPreVat + newAmount,
        totalVat: acc.totalVat + newAmountIncludedTax,
      };
    },
    { totalVat: 0, totalPreVat: 0 },
  );
  const totalUnknownSparePart = (quotation.unknownSpareParts || []).reduce(
    (acc, unknownSparePart) => {
      if (unknownSparePart.recipient?.recipientType !== RecipientType.OWNER) {
        return acc;
      }

      const newAmount = unknownSparePart.preTaxAmount.amount;
      const newAmountIncludedTax = unknownSparePart.amountIncludedTax.amount;

      return {
        totalPreVat: acc.totalPreVat + newAmount,
        totalVat: acc.totalVat + newAmountIncludedTax,
      };
    },
    { totalVat: 0, totalPreVat: 0 },
  );
  const totalSparePartLines = (quotation.sparePartLines || []).reduce(
    (acc, sparePartQuotationLine) => {
      if (sparePartQuotationLine.recipient?.data !== RecipientType.OWNER) {
        return acc;
      }

      if (
        sparePartQuotationLine.isSelectedForRepair ||
        sparePartQuotationLine.isRequiredForRepair
      ) {
        const amountPreVat = round(
          sparePartQuotationLine.recommendedSalePriceIncludingTaxes.amount /
            (1 + vatRate),
          2,
        );

        return {
          totalPreVat: acc.totalPreVat + amountPreVat,
          totalVat:
            acc.totalVat +
            sparePartQuotationLine.recommendedSalePriceIncludingTaxes.amount,
        };
      }

      return acc;
    },
    { totalVat: 0, totalPreVat: 0 },
  );
  const totalDiscount = (quotation.discounts || []).reduce(
    (acc, discount) => {
      const newAmount = discount.preTaxAmount.amount;
      const newAmountIncludedTax = discount.amountIncludedTax.amount;

      if (discount.recipient?.recipientType !== RecipientType.OWNER) {
        return acc;
      }

      return {
        totalPreVat: acc.totalPreVat + newAmount,
        totalVat: acc.totalVat + newAmountIncludedTax,
      };
    },
    { totalVat: 0, totalPreVat: 0 },
  );

  const isTotalAlreadyPaidArchived = [
    QuotationState.APPROVED,
    QuotationState.REJECTED,
    QuotationState.PAID,
    QuotationState.CANCELLED,
  ].includes(quotation.state);

  const totalAlreadyPaid = isTotalAlreadyPaidArchived
    ? quotation.alreadyPaidAmountOnApproval?.amount || 0
    : ReverseMoneyHelper.add(
        ReverseMoneyHelper.abs(alreadyPaidAmountInPreviousQuotation),
        ReverseMoneyHelper.abs(deductibleAmountFromClaim),
      ).amount;

  const bonus =
    quotation?.bonus && !quotation.isBonusRemoved
      ? quotation.bonus.amountIncludedTax
      : undefined;

  const subtotalPreVat =
    totalAdministrativeFees.totalPreVat +
    totalService.totalPreVat +
    totalUnknownService.totalPreVat +
    totalUnknownSparePart.totalPreVat +
    totalSparePartLines.totalPreVat;
  const subtotalVat =
    totalAdministrativeFees.totalVat +
    totalService.totalVat +
    totalUnknownService.totalVat +
    totalUnknownSparePart.totalVat +
    totalSparePartLines.totalVat;
  const totalAmountToBePaid = parseFloat(
    (
      subtotalVat -
      Math.abs(totalDiscount.totalVat) -
      Math.abs(totalAlreadyPaid) -
      Math.abs(bonus?.amount || 0)
    ).toFixed(2),
  );

  const { sellerConfiguration: seller } = useGetSellerConfiguration({
    sellerId: fileInfo?.sellerId,
  });

  const currency = seller?.currency || CURRENCIES.EUR;

  return {
    subtotalPreVat: { amount: subtotalPreVat, currency },
    subtotalVat: { amount: subtotalVat, currency },
    totalDiscountIncludedTax: quotation.totalDiscountIncludedTax,
    alreadyPaid: { amount: totalAlreadyPaid, currency },
    totalAmountToBePaid: {
      amount: totalAmountToBePaid,
      currency,
    },
    bonus,
  };
};
